import { isVerifiedSanityCategory } from '../../../lib/preservedUserDecisions';
/**
 * RerunProviderDataStatus — Reader Polish Prompt 5.
 *
 * Two-column status grid mounted at the top of the rerun panel's
 * step 1 ("Provider data status"). Replaces the previous full-width
 * "✓ No Data Gaps Detected" mega-card, which conflated two distinct
 * gap surfaces:
 *
 *   - LEFT: Providers (climate / grid / regulatory etc.) — driven by
 *     the report's SED gap count.
 *   - RIGHT: Documents — driven by `evidenceConfidence.missingCriticalIds`
 *     plus unresolved sanity findings. Soft warnings, not blockers.
 *
 * Showing both side-by-side lets the reader see why a report can have
 * "all providers verified" AND a 75% confidence cap: the cap comes
 * from the document side, not the provider side.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';

export interface RerunProviderDataStatusProps {
  report: SiteRagReport;
  /** Number of unresolved provider gaps. The parent already computes
   *  this from `report.sedGaps` + the conclusion gap list (see
   *  FeasibilityRerunPanel). Passed in so the panel and the status
   *  grid agree on a single source of truth. */
  providerGapCount: number;
  /** CTA fired from the Documents column when the critical-doc
   *  warning surfaces. Optional — when omitted the row renders
   *  without an Upload affordance. */
  onUploadDoc?: () => void;
  /** CTA fired from the Documents column when the unresolved-finding
   *  warning surfaces. Anchor-link by default; when supplied this
   *  handler runs instead. */
  onReviewFindings?: () => void;
}

interface ColumnState {
  tone: 'good' | 'warn';
  title: string;
  body: string;
  /** Optional CTA pair rendered as small inline buttons. */
  cta?: { label: string; onClick: () => void; testId: string };
}

function buildProviderColumn(report: SiteRagReport, providerGapCount: number): ColumnState {
  if (providerGapCount === 0) {
    // Count visible providers as a small reassurance line. Pull from
    // providerStatuses when available (falls back to the SED section
    // count which approximates "how many sources contributed").
    const providerCount = report.providerStatuses?.length
      ?? Math.max(1, Math.round((report.sedCompleteness ?? 0) * 16));
    return {
      tone: 'good',
      title: 'Providers',
      body: `All ${providerCount} sources returned verified data`,
    };
  }
  return {
    tone: 'warn',
    title: 'Providers',
    body: `${providerGapCount} provider gap${providerGapCount === 1 ? '' : 's'} unresolved — see list below`,
  };
}

function buildDocumentsColumn(
  report: SiteRagReport,
  onUploadDoc?: () => void,
  onReviewFindings?: () => void,
): ColumnState {
  // Critical-doc gap wins over unresolved findings — it's the surface
  // that cap-locks the report's confidence. The existing per-row
  // detail still renders below if the user wants the breakdown.
  const missingDocs = report.evidenceConfidence?.missingCriticalIds?.length ?? 0;
  if (missingDocs > 0) {
    const cap = report.evidenceConfidence?.cap ?? 0;
    const docNoun = missingDocs === 1 ? 'doc' : 'docs';
    return {
      tone: 'warn',
      title: 'Documents',
      body: `${missingDocs} critical ${docNoun} missing — confidence capped at ${(cap * 100).toFixed(0)}%`,
      cta: onUploadDoc ? { label: 'Upload now', onClick: onUploadDoc, testId: 'rerun-status-upload' } : undefined,
    };
  }

  const unresolvedFindings = (report.sanityFindings ?? []).filter(f => !f.resolution && !isVerifiedSanityCategory(f.category) && (f.severity as string) !== 'verified').length;
  if (unresolvedFindings > 0) {
    return {
      tone: 'warn',
      title: 'Documents',
      body: `${unresolvedFindings} unresolved finding${unresolvedFindings === 1 ? '' : 's'} from prior run — review before rerun`,
      cta: onReviewFindings ? { label: 'Review', onClick: onReviewFindings, testId: 'rerun-status-review' } : undefined,
    };
  }

  // Clean: every critical doc in place + every finding resolved.
  return {
    tone: 'good',
    title: 'Documents',
    body: 'No critical document gaps · no unresolved findings',
  };
}

const TONE_CLASSES: Record<ColumnState['tone'], { border: string; bg: string; iconColor: string; titleColor: string }> = {
  good: { border: 'border-success/40', bg: 'bg-success/10',  iconColor: 'text-success', titleColor: 'text-success' },
  warn: { border: 'border-warning/40', bg: 'bg-warning/10',  iconColor: 'text-warning', titleColor: 'text-warning' },
};

export function RerunProviderDataStatus({
  report, providerGapCount, onUploadDoc, onReviewFindings,
}: RerunProviderDataStatusProps) {
  const left = buildProviderColumn(report, providerGapCount);
  const right = buildDocumentsColumn(report, onUploadDoc, onReviewFindings);

  return (
    <div
      className="grid grid-cols-2 gap-2"
      data-testid="rerun-provider-data-status"
    >
      {[left, right].map((col, idx) => {
        const tone = TONE_CLASSES[col.tone];
        const Icon = col.tone === 'good' ? CheckCircle2 : AlertTriangle;
        const sideTestId = idx === 0 ? 'rerun-status-providers' : 'rerun-status-documents';
        return (
          <div
            key={col.title}
            className={`p-3 border ${tone.border} ${tone.bg} flex flex-col gap-1`}
            data-testid={sideTestId}
            data-tone={col.tone}
          >
            <div className="flex items-center gap-1.5">
              <Icon className={`w-3.5 h-3.5 ${tone.iconColor}`} aria-hidden="true" />
              <span className={`type-section ${tone.titleColor}`}>
                {col.title}
              </span>
            </div>
            <p className="text-[10px] text-text-primary leading-relaxed">
              {col.body}
            </p>
            {col.cta && (
              <button
                type="button"
                onClick={col.cta.onClick}
                className="self-start mt-1 type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                data-testid={col.cta.testId}
              >
                {col.cta.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
